/**
 * Sharing a battle (docs/SHARE.md, D-145).
 *
 * A share is a link plus one line of text, sent from the reveal through the OS
 * share sheet. The link names a matchup — two roster slugs in the sender's
 * order and the age band it was played at — and nothing else: no winner, no
 * story text, no id, nothing about the child. Opening it runs exactly what
 * Fight! runs (App.jsx), so the recipient still gets to make the call (D-135).
 *
 *   https://animal-battles-app.netlify.app/battle/lion/tiger?age=6
 *
 * Everything here is pure. `pendingLink` is the one exception in spirit: it is
 * the current address, read ONCE when this module loads and frozen, so the age
 * band is right on the very first render (useGame's initial state reads it)
 * and App can clean the address bar without losing the pair.
 */

export const LINK_PREFIX = 'battle'
export const AGE_BANDS = ['2', '4', '6']
/** A path longer than this is not one of ours; two slugs and the prefix fit in far less. */
export const MAX_PATH = 128
/** The shape of a roster slug — the same rule the analytics allowlist applies. Exact roster membership is checked by the caller. */
const SLUG_RE = /^[a-z][a-z0-9-]{0,39}$/

/**
 * Parse a battle link out of a Location (or anything with `pathname` and
 * `search`). Returns null when the address is not a battle link at all, and
 * otherwise `{ a, b, age }` where each slug is a lowercase, slug-shaped string
 * or null, and `age` is one of the three bands or null. Slugs are matched by
 * shape only here — never by the speech or search matchers (D-075): a link is
 * machine-written, so the caller requires exact membership in the roster.
 */
export function readBattleLink(location) {
  const pathname = String(location?.pathname || '')
  if (pathname.length > MAX_PATH) return null
  // Empty segments are dropped, so `/battle/lion/tiger/` and `//battle/lion/tiger` still read.
  const segments = pathname
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
  if (segments[0] !== LINK_PREFIX) return null
  // Exactly the prefix and up to two slugs; anything longer is not a battle link we wrote.
  if (segments.length > 3) return { a: null, b: null, age: readAge(location) }
  const slug = (s) => (typeof s === 'string' && SLUG_RE.test(s) ? s : null)
  return { a: slug(segments[1]), b: slug(segments[2]), age: readAge(location) }
}

function readAge(location) {
  try {
    const age = new URLSearchParams(String(location?.search || '')).get('age')
    return AGE_BANDS.includes(age) ? age : null
  } catch {
    return null
  }
}

/** A link is a whole battle when it names two different slug-shaped animals. */
export function isBattleLink(link) {
  return !!(link && link.a && link.b && link.a !== link.b)
}

/** The link for a battle, in the sender's order. Origin + path + age, and never the sender's ?theme=. */
export function battleUrl({ a, b, age, origin }) {
  return `${origin}/${LINK_PREFIX}/${encodeURIComponent(a)}/${encodeURIComponent(b)}?age=${encodeURIComponent(age)}`
}

/**
 * The one line that travels with the link. The title is the app-written
 * "{A} vs. {B}" (APP_WRITTEN in server/llm/prompt.js), so no model prose and
 * no winner ever leaves the device: the text asks the question the recipient
 * gets to answer (D-135). "Who wins?" was SPEC's own working title.
 */
export function shareText(title) {
  return `${title} — who wins?`
}

/**
 * The battle link this page was opened with, read once and frozen. Null when the
 * page was opened at any other address (which is every ordinary launch).
 */
export const pendingLink = Object.freeze(typeof window !== 'undefined' && window.location ? readBattleLink(window.location) : null)

export default { readBattleLink, isBattleLink, battleUrl, shareText, pendingLink, LINK_PREFIX, MAX_PATH }
